import { DatePipe } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  Observable,
  filter,
  tap,
  mergeMap,
  combineLatest,
  fromEvent,
} from 'rxjs';

import {
  DprActivityModelReport,
  DprStatusTrackerReportModel,
  DprFreeTextFieldReport,
} from '@report/models';
import { ReportActivitiesService } from '@report/services';
import { ReportStatusTrackerService } from '@report/services/report-status-tracker.service';
import {
  NamingConventionType,
  ReportOrderItemSectionType,
  ReportOrderItemType,
  UtcDateTimeOptions,
} from '@shared/enums';
import { ProjectSetup } from '@shared/models';
import {
  DprDetails,
  DprTableOfColumns,
  NamingConventionDprDetails,
} from '@shared/models/dpr';
import {
  FreeTextFieldWithValues,
  ReportConfiguration,
  ReportOrderItem,
  TableOfColumnItemValue,
  TableOfColumnWithData,
} from '@shared/models/project-setup';
import { CustomFilenamePipe } from '@shared/pipes';
import { NamingConventionService } from '@shared/services/dpr';
import { OfflineFacade, UserDetailsFacade } from '@shared/state/facades';
import { isDataTypeSummarizable, toUTCDate } from '@shared/utils';

import cloneDeep from 'lodash.clonedeep';
import { ReportLogOfEvents } from '@report/models/report-of-event-log';
import { ReportOfEventLogService } from '@report/services/report-of-event-log.service';

@UntilDestroy()
@Component({
  selector: 'app-report-container',
  templateUrl: './report-container.component.html',
  styleUrls: ['./report-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportContainerComponent implements OnInit {
  dprDetails$!: Observable<DprDetails>;
  selectedSubproject$ = this.userDetailsFacade.query.selectedSubproject$;
  projectSetup$ = this.userDetailsFacade.query.projectSetup$;
  projectSetup: ProjectSetup;
  dprDetails!: DprDetails;
  reportTablesOrder: ReportOrderItem[] = [];
  activitiesModel$: Observable<DprActivityModelReport[]>;
  reportLogOfEvents$: Observable<ReportLogOfEvents[]>;
  statusTrackerModel$: Observable<DprStatusTrackerReportModel[]>;

  shouldAddWatermark: boolean;
  reportOrderItemType = ReportOrderItemType;
  enableEventsLogActivityGroupsOnly: boolean;

  constructor(
    private activatedRoute: ActivatedRoute,
    private offlineFacade: OfflineFacade,
    private userDetailsFacade: UserDetailsFacade,
    private namingConventionService: NamingConventionService,
    private reportActivitiesService: ReportActivitiesService,
    private reportLogOfEventsService: ReportOfEventLogService,
    private statusTrackerService: ReportStatusTrackerService,
    private datePipe: DatePipe,
    private customFilenamePipe: CustomFilenamePipe
  ) {}

  //#region Getters

  get dprDate(): Date {
    return toUTCDate(
      new Date(this.dprDetails.dprDateTime),
      UtcDateTimeOptions.UtcDateWithoutTime
    );
  }

  get isRenameReportFileEnabled(): boolean {
    return this.namingConventionService.isRenameReportFileEnabled(
      this.projectSetup.namingConvention
    );
  }

  get customFilename(): string {
    const filenameDetails: NamingConventionDprDetails = {
      dprDate: this.dprDate,
      dprRevision: this.dprDetails.revision,
      dprNumber: this.dprDetails.dprDisplayId,
    };

    return this.customFilenamePipe.transform(
      NamingConventionType.Report,
      this.projectSetup.namingConvention.namingConventionItemList.find(
        (x) => x.key === NamingConventionType.Report
      ).value,
      filenameDetails
    );
  }

  get defaultFilename(): string {
    return `DPR_Report_${this.datePipe.transform(
      this.dprDate,
      'yyyy-MM-dd'
    )}.pdf`;
  }

  getFreeTextFieldCustomname(identifier: number): string {
    const customName: string = this.projectSetup.freeTextFields.find(
      (x) => x.identifier === identifier && x.isActive
    )?.name;
    return customName ?? '';
  }

  getFreeTextFieldSubTabs(
    freeTextFields: FreeTextFieldWithValues[],
    identifier: number
  ): DprFreeTextFieldReport[] {
    const freeTextFieldSubTabs: DprFreeTextFieldReport[] = [];
    const freeTextFieldId = this.getFreeTextFieldId(identifier);

    const freeTextField = freeTextFields.find((x) => x.id === freeTextFieldId);
    freeTextField?.values.forEach((y) => {
      const subtabComment = this.dprDetails.dprFreeTextFields
        .find((x) => x.freeTextFieldId === freeTextFieldId)
        ?.dprFreeTextFieldDetails.find(
          (x) => x.freeTextFieldItemId === y.id
        )?.comment;

      if (subtabComment) {
        freeTextFieldSubTabs.push({
          comment: subtabComment,
          name: y.name,
        });
      }
    });

    return freeTextFieldSubTabs;
  }

  public getTableOfColumn(identifier: number): DprTableOfColumns {
    const tableOfColumnId = this.getTableOfColumnId(identifier);

    let dprTableOfColumn: DprTableOfColumns;

    if (tableOfColumnId) {
      dprTableOfColumn = this.dprDetails.dprTableOfColumns.find(
        (x) => x.tableOfColumnId === tableOfColumnId
      );
    }

    return dprTableOfColumn;
  }

  public getTableOfColumnWithData(identifier: number): TableOfColumnWithData {
    return this.projectSetup.tableOfColumns.find(
      (x) => x.identifier === identifier && x.isActive
    );
  }

  public getTableOfColumnItemDataTypeValueList(
    tableOfColumn: TableOfColumnWithData
  ): TableOfColumnItemValue[] {
    return tableOfColumn.tableOfColumnItems.flatMap(
      (item) => item.values?.filter((v) => v.isActive) ?? []
    );
  }

  public getTableOfColumnSummaryEnabled(
    tableOfColumn: TableOfColumnWithData
  ): boolean {
    const summaryEnabled =
      tableOfColumn?.summaryEnabled &&
      tableOfColumn.tableOfColumnItems.some(
        (x) =>
          x.tableOfColumnId === tableOfColumn.id &&
          isDataTypeSummarizable(x.dataTypeId) &&
          x.includeInSummary
      );

    return summaryEnabled;
  }

  //#endregion

  ngOnInit(): void {
    this.activatedRoute.queryParamMap
      .pipe(
        untilDestroyed(this),
        mergeMap((params) => {
          this.shouldAddWatermark = JSON.parse(params.get('addWatermark'));
          this.dprDetails$ = this.offlineFacade.getDprDetails(
            params.get('reportId')
          );
          return combineLatest([
            this.dprDetails$,
            this.userDetailsFacade.query.selectedSubproject$,
          ]);
        }),
        filter(([dprDetails, _]) => !!dprDetails),
        tap(([dprDetails, selectedSubproject]) => {
          this.dprDetails = dprDetails;
          this.projectSetup = selectedSubproject.data;

          this.getReportTablesOrder(
            selectedSubproject.data.reportConfiguration
          );

          this.enableEventsLogActivityGroupsOnly =
            selectedSubproject.data.reportConfiguration.enableEventsLogActivityGroupsOnly;

          this.activitiesModel$ =
            this.reportActivitiesService.constructActivitiesModel([
              ...dprDetails.dprActivities,
              ...dprDetails.dprParallelActivities,
            ]);

          this.reportLogOfEvents$ =
            this.reportLogOfEventsService.constructReportOfEventLog(
              [
                ...dprDetails.dprActivities,
                ...dprDetails.dprParallelActivities,
              ],
              dprDetails.dprActivitiesDurationHistory,
              selectedSubproject.data
            );

          this.statusTrackerModel$ =
            this.statusTrackerService.constructStatusTrackerModel(
              dprDetails.dprStatusTracker
            );

          this.openPrintWindow();
        })
      )
      .subscribe();

    // We need this event in order to detect when the tab is being closed from the X button of the tab.
    // When that happenes we are going to listen to the storage event change in the parent tab
    // from which the report page was opened
    window.addEventListener('beforeunload', () => {
      localStorage.setItem('reportPageTabClosed', 'true');
    });
  }

  openPrintWindow(): void {
    // Initiate the Print Window
    setTimeout(window.print, 500);

    // this is needed to close the window after
    // the print window print modal was closed
    fromEvent(window, 'afterprint')
      .pipe(
        untilDestroyed(this),
        tap(() => {
          window.close();
        })
      )
      .subscribe();
  }

  //#region Private methods

  private getTableOfColumnId(identifier: number): number {
    const tableOfColumnId = this.projectSetup.tableOfColumns.find(
      (x) => x.identifier === identifier && x.isActive
    )?.id;

    return tableOfColumnId;
  }

  private getFreeTextFieldId(identifier: number): number {
    const freeTextFieldId = this.projectSetup.freeTextFields.find(
      (x) => x.identifier === identifier && x.isActive
    )?.id;

    return freeTextFieldId;
  }

  private getReportTablesOrder(reportConfiguration: ReportConfiguration): void {
    this.reportTablesOrder = [
      ...(reportConfiguration.reportOrder.useDefaultOrder
        ? this.getReportOrderItemType(
            reportConfiguration.reportOrder.defaultOrder
          )
        : this.getReportOrderItemType(
            reportConfiguration.reportOrder.order.filter((x) => x.includeInPDF)
          )),
    ];

    // Remove Appendix and Attachments from list. They are not rendered in Offline
    const appendixIndex = this.reportTablesOrder.findIndex(
      (i) =>
        i.identifier === 70 &&
        i.reportOrderItemSectionType === ReportOrderItemSectionType.Basic
    );
    if (appendixIndex !== -1) {
      this.reportTablesOrder.splice(appendixIndex, 1);
    }

    const attachmentIndex = this.reportTablesOrder.findIndex(
      (i) =>
        i.identifier === 100 &&
        i.reportOrderItemSectionType === ReportOrderItemSectionType.Basic
    );
    if (attachmentIndex !== -1) {
      this.reportTablesOrder.splice(attachmentIndex, 1);
    }
  }

  private getReportOrderItemType(
    reportOrderItem: ReportOrderItem[]
  ): ReportOrderItem[] {
    const reportOrderItemCopy = cloneDeep(reportOrderItem);

    reportOrderItemCopy.forEach((r: ReportOrderItem) => {
      switch (r.reportOrderItemSectionType) {
        case ReportOrderItemSectionType.ActivityStream:
          r.reportOrderItemType = ReportOrderItemType.Activities;
          break;
        case ReportOrderItemSectionType.TableOfColumn:
          r.reportOrderItemType = ReportOrderItemType.TableOfColumns;
          break;
        case ReportOrderItemSectionType.FreeTextField:
          r.reportOrderItemType = ReportOrderItemType.FreeTextFields;
          break;
        case ReportOrderItemSectionType.Basic:
        default:
          r.reportOrderItemType = r.identifier;
          break;
      }
    });

    return reportOrderItemCopy;
  }

  //#endregion
}
